import { useEffect, useState } from 'react';
import { api } from '../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { formatMoney } from '../lib/currency';
import { toast } from '../lib/toast';

export default function SubscriptionPaymentHistory({ subscription, onClose, onChanged, readOnly = false }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setLoading(true); setError('');
    try {
      const { data } = await api.get(`/subscriptions/${subscription.id}/payments`);
      setPayments(data.payments || []);
    } catch (e) { setError(e.response?.data?.detail || 'No se pudo cargar el historial. Intenta de nuevo.'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [subscription.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const cancel = async () => {
    if (busy || !selected) return;
    setBusy(true);
    try {
      await api.delete(`/subscriptions/${subscription.id}/payments/${selected.id}`);
      toast.success('Pago anulado y saldo actualizado');
      setSelected(null);
      await load();
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo completar la anulación. Intenta de nuevo.'); }
    finally { setBusy(false); }
  };
  return <Dialog open onOpenChange={open => { if (!open && !busy) onClose(); }}>
    <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto rounded-3xl bg-white p-6">
      <DialogHeader><DialogTitle>Historial de pagos · {subscription.name}</DialogTitle></DialogHeader>
      <p className="text-sm text-slate-600">Anular retira solo el registro de Aorra y ajusta su cuenta o tarjeta. No solicita un reembolso al proveedor.</p>
      {loading ? <p role="status">Cargando pagos…</p> : error ? <div role="alert">{error}<Button onClick={load}>Reintentar</Button></div> :
        payments.length === 0 ? <p>No hay pagos registrados.</p> : <ul className="space-y-3">
          {[...payments].reverse().map(payment => <li key={payment.id} className="rounded-xl border border-slate-200 p-4">
            <p className="font-semibold">{payment.currency} {formatMoney(payment.amount, payment.currency)} · {payment.description}</p>
            <p className="text-sm text-slate-600">{payment.date ? new Date(payment.date).toLocaleString('es-CO') : 'Sin fecha'}</p>
            <p className="text-sm">{payment.funding_name}{payment.funding_currency && <> · {payment.funding_currency} {formatMoney(payment.funding_amount || 0, payment.funding_currency)}</>}</p>
            {payment.status === 'cancelled' ? <p className="text-sm font-semibold text-slate-500">Anulado</p> :
              !payment.can_cancel ? <p className="text-sm text-amber-800">{payment.cancellation_error}</p> :
              selected?.id === payment.id ? <div className="mt-3 space-y-3" role="group" aria-label="Confirmar anulación">
                <p className="text-sm">Se retirará este movimiento y se ajustará su cuenta o tarjeta. La suscripción y los demás pagos se conservan.</p>
                <p className="text-sm">{payment.restores_billing_date ? 'Se restaurará la fecha de cobro anterior a este pago.' : 'La próxima fecha de cobro se conserva; puedes editarla en la suscripción.'}</p>
                <div className="flex flex-wrap gap-2">
                  <Button onClick={() => setSelected(null)} disabled={busy} variant="outline">Conservar pago</Button>
                  <Button onClick={cancel} disabled={busy || readOnly} variant="destructive">{busy ? 'Anulando…' : 'Confirmar anulación'}</Button>
                </div>
              </div> : <Button onClick={() => setSelected(payment)} disabled={busy || readOnly} variant="outline" className="mt-3">Anular este pago</Button>}
          </li>)}
        </ul>}
    </DialogContent>
  </Dialog>;
}
